import { ClientPrivacyCard } from './admin/Privacy';
import { can, clientName } from '@clim/client-core';
import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ClientForm } from '../components/ClientForm';
import { DuplicateAlert } from './DossierDetail';
import { Empty, Input, PendingDot, StatusBadge } from '../components/ui';
import { dossierLabel } from '@clim/client-core';
import { useEngine, useReady } from '../lib/session';

export function ClientsPage() {
  const engine = useEngine();
  const { me } = useReady();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  // tri de la base : nos clients / clients des partenaires (sous-traitance de pose) / un partenaire
  const [origin, setOrigin] = useState<'all' | 'own' | 'partners' | string>('all');
  const [creating, setCreating] = useState(false);
  // tri et filtres par commune / code postal (communes officielles : orthographe unique)
  const [city, setCity] = useState('');
  const [cp, setCp] = useState('');
  const [sort, setSort] = useState<'name' | 'city' | 'postalCode'>('name');
  const all = engine.list('client');
  const cities = [...new Set(all.map((c) => c.city).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b, 'fr'));
  const cps = [...new Set(all.map((c) => c.postalCode).filter((x): x is string => !!x))].sort();
  const partners = engine.list('reason').filter((r) => r.category === 'partner').sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  const clients = useMemo(() => {
    const term = q.trim().toLowerCase();
    return engine
      .list('client')
      .filter((c) =>
        origin === 'all' ? true : origin === 'own' ? !c.partnerId : origin === 'partners' ? !!c.partnerId : c.partnerId === origin,
      )
      .filter((c) => !term || [clientName(c), c.phone, c.email, c.city, c.postalCode].some((v) => v?.toLowerCase().includes(term)))
      .filter((c) => !city || c.city === city)
      .filter((c) => !cp || c.postalCode === cp)
      .sort(
        (a, b) =>
          (sort === 'city'
            ? (a.city ?? '\uffff').localeCompare(b.city ?? '\uffff', 'fr')
            : sort === 'postalCode'
              ? (a.postalCode ?? '\uffff').localeCompare(b.postalCode ?? '\uffff')
              : 0) || clientName(a).localeCompare(clientName(b), 'fr'),
      );
  }, [engine, engine.version, q, origin, city, cp, sort]);
  const partnerLabel = (id: string | null) => (id ? (engine.get('reason', id)?.label ?? 'Partenaire') : null);

  if (creating) {
    return (
      <section>
        <h1>Nouveau client</h1>
        <div className="card">
          <ClientForm onSaved={(id) => navigate(`/clients/${id}`)} onCancel={() => setCreating(false)} />
        </div>
      </section>
    );
  }
  return (
    <section>
      <div className="page-head">
        <h1>Clients</h1>
        {can(me, 'client.create') && (
          <button className="btn primary" onClick={() => setCreating(true)}>
            + Nouveau client
          </button>
        )}
      </div>
      <div className="filters">
        <Input type="search" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
        {partners.length > 0 && (
          <select className="input" aria-label="Origine des clients" value={origin} onChange={(e) => setOrigin(e.target.value)}>
            <option value="all">Tous les clients (y compris ceux des partenaires)</option>
            <option value="own">Clients en direct (hors partenaires)</option>
            <option value="partners">Clients des partenaires seulement</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                Partenaire : {p.label}
              </option>
            ))}
          </select>
        )}
        <select className="input" aria-label="Commune" value={city} onChange={(e) => setCity(e.target.value)}>
          <option value="">Toutes les communes</option>
          {cities.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select className="input" aria-label="Code postal" value={cp} onChange={(e) => setCp(e.target.value)}>
          <option value="">Tous les codes postaux</option>
          {cps.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <select className="input" aria-label="Trier par" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="name">Trier par nom</option>
          <option value="city">Trier par commune</option>
          <option value="postalCode">Trier par code postal</option>
        </select>
        <span className="muted">{clients.length} client(s)</span>
      </div>
      {clients.length === 0 ? (
        <Empty>Aucun client.</Empty>
      ) : (
        <ul className="list">
          {clients.map((c) => (
            <li key={c.id}>
              <Link className="row" to={`/clients/${c.id}`}>
                <div className="row-main">
                  <strong>{clientName(c)}</strong>
                  <span className="muted">{[c.phone, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span>
                </div>
                {c.partnerId && <span className="badge partner">Partenaire : {partnerLabel(c.partnerId)}</span>}
                <PendingDot show={engine.isPending('client', c.id)} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function ClientDetailPage() {
  const { id = '' } = useParams();
  const engine = useEngine();
  const { me } = useReady();
  const [editing, setEditing] = useState(false);
  const c = engine.get('client', id);
  if (!c) return <Empty>Client introuvable (ou plus accessible).</Empty>;
  const dossiers = engine.list('dossier').filter((d) => d.clientId === id);
  const territory = engine.get('territory', c.territoryId);

  if (editing) {
    return (
      <section>
        <h1>Modifier le client</h1>
        <div className="card">
          <ClientForm initial={c} onSaved={() => setEditing(false)} onCancel={() => setEditing(false)} />
        </div>
      </section>
    );
  }
  return (
    <section>
      <div className="page-head">
        <h1>
          {clientName(c)} <PendingDot show={engine.isPending('client', c.id)} />
        </h1>
        {can(me, 'client.update') && (
          <button className="btn" onClick={() => setEditing(true)}>
            Modifier
          </button>
        )}
      </div>
      {can(me, 'dossier.control') && <DuplicateAlert clientId={c.id} />}
      <div className="card">
        <dl className="kv">
          <dt>Téléphone</dt>
          <dd>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : '—'}</dd>
          <dt>E-mail</dt>
          <dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'}</dd>
          <dt>Adresse</dt>
          <dd>{[c.addressLine1, c.addressLine2, c.postalCode, c.city].filter(Boolean).join(', ') || '—'}</dd>
          {c.extraPhones.map((p, i) => (
            <Fragment key={i}>
              <dt>{p.label ?? 'Autre numéro'}</dt>
              <dd>
                <a href={`tel:${p.number}`}>{p.number}</a>
              </dd>
            </Fragment>
          ))}
          {c.contactName && (
            <>
              <dt>Contact</dt>
              <dd>{c.contactName}</dd>
            </>
          )}
          <dt>Territoire</dt>
          <dd>{territory?.name}</dd>
          {c.partnerId && (
            <>
              <dt>Client du partenaire</dt>
              <dd>{engine.get('reason', c.partnerId)?.label ?? '—'}</dd>
            </>
          )}
          {c.siret && (
            <>
              <dt>SIRET</dt>
              <dd>{c.siret}</dd>
            </>
          )}
          <dt>Offres commerciales</dt>
          <dd>{c.marketingConsent ? 'Accepte' : 'Refuse'}</dd>
        </dl>
        {c.notes && <p className="pre">{c.notes}</p>}
      </div>
      {can(me, 'privacy.manage') && !engine.isPending('client', c.id) && <ClientPrivacyCard clientId={c.id} name={clientName(c)} />}
      <div className="page-head">
        <h2>Dossiers</h2>
        {can(me, 'dossier.create') && (!c.partnerId || can(me, 'dossier.schedule')) && (
          <Link className="btn small" to={`/dossiers/nouveau?client=${c.id}`}>
            {c.partnerId ? '+ Chantier partenaire' : '+ Dossier'}
          </Link>
        )}
      </div>
      {dossiers.length === 0 ? (
        <Empty>Aucun dossier.</Empty>
      ) : (
        <ul className="list">
          {dossiers.map((d) => (
            <li key={d.id}>
              <Link className="row" to={`/dossiers/${d.id}`}>
                <div className="row-main">
                  <strong>{dossierLabel(d, engine.get('dossier_type', d.dossierTypeId)?.label)}</strong>
                  <span className="muted">{d.reference ?? 'Réf. provisoire'}</span>
                </div>
                <StatusBadge status={d.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
